import {
  getSpacing,
  SpacerValueOrKey,
} from "@faire/web--source/slate/spacing/getSpacing";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import cn from "classnames";
import React from "react";

import { isResponsiveProp, ResponsiveProp } from "../Theme/ResponsiveProp";

import * as styles from "./styles.css";

interface ISize {
  size: ResponsiveProp<SpacerValueOrKey>;
  width?: never;
  height?: never;
}

interface IWidth {
  width: ResponsiveProp<SpacerValueOrKey>;
  height?: never;
  size?: never;
}

interface IHeight {
  height: ResponsiveProp<SpacerValueOrKey>;
  width?: never;
  size?: never;
}

type FixedSpacerProps = ISize | IWidth | IHeight;

export type ISpacerProps = Omit<
  React.AllHTMLAttributes<HTMLDivElement>,
  "as" | "size" | "height" | "width"
> &
  FixedSpacerProps;

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/component/spacer/web/spacer)
 */
export const Spacer = React.forwardRef<HTMLDivElement, ISpacerProps>(
  (
    {
      height: heightProp,
      width: widthProp,
      size: sizeProp,
      className,
      style: styleProp,
      children,
      ...rest
    },
    ref
  ) => {
    let flexBasis: styles.Sprinkles["flexBasis"] | undefined;
    let minWidth: styles.Sprinkles["minWidth"] | undefined;
    let minHeight: styles.Sprinkles["minHeight"] | undefined;
    let width: styles.Sprinkles["width"] | undefined;
    let height: styles.Sprinkles["height"] | undefined;
    const vars: Record<string, string | undefined> = {};

    const definedSize = sizeProp ?? widthProp ?? heightProp;
    if (definedSize != null) {
      let staticSize: styles.Sprinkles["flexBasis"];
      if (isResponsiveProp(definedSize)) {
        vars[styles.sizeVar] = getSpacing(definedSize.mobileAndAbove);
        vars[styles.sizeTabletVar] =
          definedSize.tabletAndAbove != null
            ? getSpacing(definedSize.tabletAndAbove)
            : undefined;
        vars[styles.sizeDesktopVar] =
          definedSize.desktopAndAbove != null
            ? getSpacing(definedSize.desktopAndAbove)
            : undefined;
        vars[styles.sizeXLargeVar] =
          definedSize.xLargeAndAbove != null
            ? getSpacing(definedSize.xLargeAndAbove)
            : undefined;
        vars[styles.sizeXXLargeVar] =
          definedSize.xxLargeDesktop != null
            ? getSpacing(definedSize.xxLargeDesktop)
            : undefined;

        staticSize = {
          mobileAndAbove: styles.sizeVar,
          tabletAndAbove:
            definedSize.tabletAndAbove != null
              ? styles.sizeTabletVar
              : undefined,
          desktopAndAbove:
            definedSize.desktopAndAbove != null
              ? styles.sizeDesktopVar
              : undefined,
          xLargeAndAbove:
            definedSize.xLargeAndAbove != null
              ? styles.sizeXLargeVar
              : undefined,
          xxLargeDesktop:
            definedSize.xxLargeDesktop != null
              ? styles.sizeXXLargeVar
              : undefined,
        };

        flexBasis = staticSize;
      } else {
        vars[styles.sizeVar] = getSpacing(definedSize);

        flexBasis = styles.sizeVar;
        staticSize = styles.sizeVar;
      }

      if ((sizeProp ?? widthProp) != null) {
        minWidth = staticSize;
      }

      if ((sizeProp ?? heightProp) != null) {
        minHeight = staticSize;
      }

      if (widthProp) {
        height = 0;
      } else if (heightProp) {
        width = 0;
      }
    }

    return (
      <div
        ref={ref}
        style={{ ...assignInlineVars(vars), ...styleProp }}
        className={cn(
          className,
          styles.spacerBase,
          styles.sprinkles({
            flexBasis,
            minWidth,
            minHeight,
            width,
            height,
          })
        )}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

export type IGrowingSpacerProps = Omit<
  React.AllHTMLAttributes<HTMLDivElement>,
  "as"
> & {
  base?: ResponsiveProp<SpacerValueOrKey>;
};

/**
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/component/spacer/web/growing-spacer)
 */
export const GrowingSpacer = React.forwardRef<
  HTMLDivElement,
  IGrowingSpacerProps
>(({ base: sizeProp, className, style: styleProp, children, ...rest }, ref) => {
  let flexBasis: styles.Sprinkles["flexBasis"] | undefined;
  const vars: Record<string, string | undefined> = {};

  if (sizeProp != null) {
    if (isResponsiveProp(sizeProp)) {
      vars[styles.sizeVar] = getSpacing(sizeProp.mobileAndAbove);
      vars[styles.sizeTabletVar] =
        sizeProp.tabletAndAbove != null
          ? getSpacing(sizeProp.tabletAndAbove)
          : undefined;
      vars[styles.sizeDesktopVar] =
        sizeProp.desktopAndAbove != null
          ? getSpacing(sizeProp.desktopAndAbove)
          : undefined;
      vars[styles.sizeXLargeVar] =
        sizeProp.xLargeAndAbove != null
          ? getSpacing(sizeProp.xLargeAndAbove)
          : undefined;
      vars[styles.sizeXXLargeVar] =
        sizeProp.xxLargeDesktop != null
          ? getSpacing(sizeProp.xxLargeDesktop)
          : undefined;

      flexBasis = {
        mobileAndAbove: styles.sizeVar,
        tabletAndAbove:
          sizeProp.tabletAndAbove != null ? styles.sizeTabletVar : undefined,
        desktopAndAbove:
          sizeProp.desktopAndAbove != null ? styles.sizeDesktopVar : undefined,
        xLargeAndAbove:
          sizeProp.xLargeAndAbove != null ? styles.sizeXLargeVar : undefined,
        xxLargeDesktop:
          sizeProp.xxLargeDesktop != null ? styles.sizeXXLargeVar : undefined,
      };
    } else {
      vars[styles.sizeVar] = getSpacing(sizeProp);

      flexBasis = styles.sizeVar;
    }
  }

  return (
    <div
      ref={ref}
      style={{ ...assignInlineVars(vars), ...styleProp }}
      className={cn(
        className,
        styles.growingSpacerBase,
        styles.sprinkles({
          flexBasis,
        })
      )}
      {...rest}
    >
      {children}
    </div>
  );
});
